import { SignIn, useAuth } from "@clerk/react-router";
import { type FunctionComponent } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { useInitTokenGenerator } from "~/hooks/use-init-rag";
import { PRIVACY_POLICY_ROUTE, PrivacyPolicyScreen } from "./screens/privacy/privacy-policy-screen";
import { SchemaAdminScreen } from "./screens/admin/schema-admin-screen";
import { UploadAdminScreen } from "./screens/admin/upload-admin-screen";
import { DOCUMENTS_ROUTE, FORMER_DOCUMENTS_ROUTE } from "./routes";
import { VectorstoreAdminScreen } from "./screens/admin/vectorstore-admin-screen";
import { DocumentViewerScreen } from "./screens/document-viewer/document-viewer-screen";
import { HomeScreen } from "./screens/home/home-screen";
import { ConversationScreen } from "./screens/conversation/conversation-screen";
import { LexiconScreen } from "./screens/lexicon/lexicon-screen";
import { ConversationsScreen } from "./screens/conversations/conversations-screen";
import { PredefinedTasksScreen } from "./screens/predefined-tasks/predefined-tasks-screen";

export const App: FunctionComponent = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const { pathname } = useLocation();
  useInitTokenGenerator();

  // Public, and matched before the Clerk gate below: the App Store / Play Console privacy-policy URL
  // is opened by reviewers with no account, and sign-up on this Clerk instance is restricted.
  // Checked here rather than as a <Route> so it bypasses the `!isSignedIn` early return.
  if (pathname === PRIVACY_POLICY_ROUTE) {
    return <PrivacyPolicyScreen />;
  }

  if (!isLoaded) {
    return null;
  }

  // Hash routing because this gate replaces whatever route was requested: under the SDK's default
  // path routing, Clerk bases its sub-routes on the current pathname, so on `/` OAuth providers
  // redirect back to `//sso-callback` and the sign-in never completes.
  if (!isSignedIn) {
    return (
      <div className="flex h-screen items-center justify-center">
        <SignIn routing="hash" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<HomeScreen />} />
      <Route path="/conversation/:sessionId" element={<ConversationScreen />} />
      <Route path="/view-document/*" element={<DocumentViewerScreen />} />
      <Route path="/lexicon" element={<LexiconScreen />} />
      <Route path="/predefined-tasks" element={<PredefinedTasksScreen />} />
      <Route path="/conversations" element={<ConversationsScreen />} />
      <Route path="/admin/schemas" element={<SchemaAdminScreen />} />
      <Route path="/admin/uploads" element={<UploadAdminScreen />} />
      <Route path={DOCUMENTS_ROUTE} element={<VectorstoreAdminScreen />} />
      <Route path={FORMER_DOCUMENTS_ROUTE} element={<Navigate to={DOCUMENTS_ROUTE} replace />} />
    </Routes>
  );
};
